import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import Input from '../../elements/Input';
import Popover from '../Popover';
import { getInputText } from './commons';
import GuestsSelectorPanel from './GuestsSelectorPanel';

function GuestsSelector({
  showClear,
  dispatch,
  onApply,
  onClear,
  value,
  focusReferences,
  components: { panel },
  clearButton,
  applyButton,
  popoverItem,
  ...props
}) {
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const popoverRef = React.useRef(null);

  const handleClear = () => {
    dispatch({ type: 'CLEAR' });
    onClear();
  };

  const handleClose = () => {
    onApply(value);
  };

  const handleClearInput = () => {
    handleClear();
    onApply({ adults: 0, children: 0, infants: 0 });
  };

  const handleApply = () => {
    popoverRef.current.closePopover();
  };

  const { adults, children, infants } = value;

  const guestsCount = adults + children;
  const totalCount = guestsCount + infants;
  const inputText = getInputText(translate, value);
  const isClearEnabled = totalCount > 0;

  const input = (
    <Input
      type="text"
      label={translate(`cmp-guests-label`, 'Guests')}
      {...props}
      value={guestsCount > 0 ? inputText : ''}
      dataTestId="guests-filter-input"
      className={`fr-guests-filter ${className}`}
      style={style}
      rightIcons={showClear && totalCount > 0 ? ['close'] : []}
      onRightIconClick={handleClearInput}
      leftIcons={['avatar']}
      leftTabIndex={[-1]}
      leftAriaLabel={[translate('cmp-guests-aria-label-icon-avatar', 'Avatar')]}
      placeholder={translate('cmp-guests-placeholder', 'Any Guests')}
      ariaLabelDescription={translate(
        'cmp-guests-label-description',
        'Select the number of guests'
      )}
      ariaLabelInvalidDescription={translate(
        'cmp-guests-label-invalid-description',
        'Invalid format, please change the value'
      )}
      focusReferences={focusReferences}
    />
  );

  return (
    <Popover
      element={input}
      focusReferences={(ref) => {
        popoverRef.current = ref;
      }}
      onClose={handleClose}
      {...popoverItem}
    >
      <Box paddingWidth="6px" style={{ lineHeight: '3' }}>
        <GuestsSelectorPanel value={value} dispatch={dispatch} {...panel} />
        <Box className="fr-clearfix">
          {isClearEnabled && (
            <Button paddingWidth="0" color="none" floatLeft onClick={handleClear} {...clearButton}>
              {translate('cmp-guests-select-btn-clear', 'Clear')}
            </Button>
          )}
          <Button
            paddingWidth="0"
            color="none"
            floatRight
            textColor="primary"
            onClick={handleApply}
            {...applyButton}
          >
            {translate('cmp-guests-select-btn-apply', 'Apply')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}

GuestsSelector.propTypes = {
  /**
   * Props of the popover
   */
  popoverItem: PropTypes.objectOf(PropTypes.any),
  /**
   *  CSS Class to apply to input.
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Show clear button
   */
  showClear: PropTypes.bool,
  /**
   * Value of the current guests selection
   */
  value: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    infants: PropTypes.number,
  }),
  /**
   * LeftIconsProps by default
   */
  leftIconsProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for dispatching actions
   * ({ type: 'INCREASE' | 'DECREASE', field: 'adults' | 'children' | 'adults' } | { type: 'CLEAR' })
   */
  dispatch: PropTypes.func,
  /**
   * onApply function
   */
  onApply: PropTypes.func,
  /**
   * onClear input function
   */
  onClear: PropTypes.func,
  /**
   * onClear input function
   */
  onInputClear: PropTypes.func,
  /**
   * Forwarded ref
   */
  focusReferences: PropTypes.func,
  /**
   * Components settings
   */
  components: PropTypes.shape({
    panel: PropTypes.object,
  }),

  clearButton: PropTypes.objectOf(PropTypes.any),
  applyButton: PropTypes.objectOf(PropTypes.any),
};

GuestsSelector.defaultProps = {
  popoverItem: {},
  className: '',
  style: {},
  showClear: true,
  value: {
    adults: 0,
    children: 0,
    infants: 0,
  },
  leftIconsProps: {
    color: 'transparent',
  },
  dispatch: () => {},
  onApply: () => {},
  onClear: () => {},
  onInputClear: () => {},
  focusReferences: () => {},
  components: {
    panel: {},
  },
  clearButton: {},
  applyButton: {},
};

GuestsSelector.componentName = 'GuestsSelector';

export default GuestsSelector;
